import React, { useState, useEffect } from 'react';
import { X, Package, Save, Shirt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';

const InventarioLenceriaForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  editingItem, 
  mode = 'create',
  hospedaje 
}) => {
  const [formData, setFormData] = useState({
    nombre: '',
    peso: '',
    cantidad: '',
    descripcion: '',
    selectedGarmentType: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [garmentTypes, setGarmentTypes] = useState([]);
  const [isLoadingGarmentTypes, setIsLoadingGarmentTypes] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && editingItem) {
      setFormData({
        nombre: editingItem.nombre || '',
        peso: editingItem.peso || '',
        cantidad: editingItem.cantidad || '',
        descripcion: editingItem.descripcion || '',
        selectedGarmentType: null
      });
    } else {
      setFormData({
        nombre: '',
        peso: '',
        cantidad: '',
        descripcion: '',
        selectedGarmentType: null
      });
    }
    setErrors({});
  }, [mode, editingItem, isOpen]);

  useEffect(() => {
    if (isOpen && hospedaje) {
      loadGarmentTypes();
    }
  }, [isOpen, hospedaje]);

  const loadGarmentTypes = async () => {
    setIsLoadingGarmentTypes(true);
    try {
      const response = await getGarmentTypes({ 
        page: 1, 
        limit: 100, 
        query: { 
          estado: 'Activo',
          'edificio.id': hospedaje.edificio.id
        } 
      });
      setGarmentTypes(response.data || []);
    } catch (error) {
      console.error('Error cargando tipos de prenda:', error);
      setGarmentTypes([]);
    } finally {
      setIsLoadingGarmentTypes(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.selectedGarmentType) {
      newErrors.selectedGarmentType = 'Debe seleccionar un tipo de prenda';
    }

    if (!formData.peso || formData.peso <= 0) {
      newErrors.peso = 'El peso debe ser mayor a 0';
    }

    if (!formData.cantidad || formData.cantidad < 0) {
      newErrors.cantidad = 'La cantidad debe ser mayor o igual a 0';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        peso: parseFloat(formData.peso),
        cantidad: parseInt(formData.cantidad, 10),
        estado: 'Activo' // Siempre activo por defecto
      };
      
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGarmentTypeSelect = (garmentTypeId) => {
    const selectedType = garmentTypes.find(gt => gt._id === garmentTypeId);
    if (selectedType) {
      setFormData(prev => ({
        ...prev,
        selectedGarmentType: selectedType,
        nombre: selectedType.nombre,
        peso: selectedType.peso / 1000, // Convertir de gramos a kilogramos
        descripcion: selectedType.descripcion || `${selectedType.nombre} - Agregado desde tipos de prenda`
      }));
      
      // Limpiar errores relacionados
      setErrors(prev => ({
        ...prev,
        nombre: '',
        peso: ''
      }));
    }
  };


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const title = mode === 'create' ? 'Agregar Elemento' : 'Editar Elemento';
  const description = mode === 'create' 
    ? 'Agrega un nuevo elemento al inventario de lencería' 
    : 'Modifica la información del elemento seleccionado';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>{title}</span>
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          {/* Información de la propiedad - Compacta */}
          {hospedaje && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">
                <strong>{hospedaje.nombrePropiedad}</strong> • {hospedaje.edificio.nombre} • {hospedaje.coordinador.nombre}
              </div>
            </div>
          )}

          {/* Selector de Tipo de Prenda */}
          <div className="space-y-2">
            <Label>Seleccionar Tipo de Prenda *</Label>
            {isLoadingGarmentTypes ? (
              <div className="flex items-center space-x-2 p-3 border rounded-md">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">Cargando tipos de prenda...</span>
              </div>
            ) : garmentTypes.length === 0 ? (
              <div className="p-3 border rounded-md text-center text-muted-foreground">
                No hay tipos de prenda disponibles para este edificio
              </div>
            ) : (
              <Select
                value={formData.selectedGarmentType?._id || ''}
                onValueChange={handleGarmentTypeSelect}
              >
                <SelectTrigger className={errors.selectedGarmentType ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Selecciona un tipo de prenda" />
                </SelectTrigger>
                <SelectContent>
                  {garmentTypes.map((garmentType) => (
                    <SelectItem key={garmentType._id} value={garmentType._id}>
                      <div className="flex items-center space-x-2">
                        <Shirt className="h-4 w-4" />
                        <div>
                          <div className="font-medium">{garmentType.nombre}</div>
                          <div className="text-xs text-muted-foreground">
                            {garmentType.peso}g ({(garmentType.peso / 1000).toFixed(2)}kg)
                            {garmentType.descripcion && ` • ${garmentType.descripcion}`}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.selectedGarmentType && (
              <p className="text-sm text-red-600">{errors.selectedGarmentType}</p>
            )}
          </div>

          {/* Peso y Cantidad en la misma fila */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="peso">Peso (kg)</Label>
              <Input
                id="peso"
                type="text"
                value={formData.peso ? `${formData.peso} kg` : 'Selecciona tipo de prenda'}
                readOnly
                disabled
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cantidad">Cantidad *</Label>
              <Input
                id="cantidad"
                type="number"
                min="0"
                value={formData.cantidad}
                onChange={(e) => handleInputChange('cantidad', e.target.value)}
                placeholder="0"
                className={errors.cantidad ? 'border-red-500' : ''}
              />
              {errors.cantidad && (
                <p className="text-sm text-red-600">{errors.cantidad}</p>
              )}
            </div>
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => handleInputChange('descripcion', e.target.value)}
              placeholder="Descripción del elemento..."
              rows={2}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>{isSubmitting ? 'Guardando...' : 'Guardar'}</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InventarioLenceriaForm;
